import { z } from 'zod';
import type { RoleGrade } from './grade.js';

/** The five platform roles (`platform_members.role`). They authorize the console only and never
 *  open the data of an organization (docs/decisions/0011-session-contexts.md). */
export const PLATFORM_ROLE_KEYS = [
  'platform_owner',
  'platform_manager',
  'platform_support',
  'platform_sales',
  'platform_auditor',
] as const;
export const platformRoleKeySchema = z.enum(PLATFORM_ROLE_KEYS);
export type PlatformRoleKey = z.infer<typeof platformRoleKeySchema>;

export const PLATFORM_ROLE_GRADE: Record<PlatformRoleKey, RoleGrade> = {
  platform_owner: 'owner',
  platform_manager: 'admin',
  platform_support: 'operator',
  platform_sales: 'operator',
  platform_auditor: 'reader',
};

/** Console catalog, separate from the organization catalog: `console.<resource>:<action>`. */
export const CONSOLE_PERMISSIONS = [
  'console.organizations:view',
  'console.organizations:create',
  'console.organizations:create_trial',
  'console.organizations:update',
  'console.organizations:suspend',
  'console.organizations:archive',
  'console.entitlements:view',
  'console.entitlements:manage',
  'console.entitlements:trial',
  'console.support:request',
  'console.members:view',
  'console.members:manage',
  'console.audit:view',
  'console.audit:view_own',
  /** Platform settings (outgoing mail, ADR 0016): the platform owner only. */
  'console.settings:manage',
] as const;
export const consolePermissionKeySchema = z.enum(CONSOLE_PERMISSIONS);
export type ConsolePermissionKey = z.infer<typeof consolePermissionKeySchema>;

const OWNER_ONLY: readonly ConsolePermissionKey[] = [
  'console.organizations:archive',
  'console.settings:manage',
];
const ALL_BUT_OWNER_ONLY = CONSOLE_PERMISSIONS.filter((p) => !OWNER_ONLY.includes(p));

/** Table §10.3 of the milestone: what each platform role may do in the console. */
export const CONSOLE_ROLE_PERMISSIONS: Record<PlatformRoleKey, readonly ConsolePermissionKey[]> = {
  platform_owner: CONSOLE_PERMISSIONS,
  platform_manager: ALL_BUT_OWNER_ONLY,
  platform_support: [
    'console.organizations:view',
    'console.entitlements:view',
    'console.support:request',
    'console.audit:view_own',
  ],
  platform_sales: [
    'console.organizations:view',
    'console.organizations:create_trial',
    'console.entitlements:view',
    'console.entitlements:trial',
  ],
  platform_auditor: [
    'console.organizations:view',
    'console.entitlements:view',
    'console.members:view',
    'console.audit:view',
  ],
};

export function isConsolePermission(key: string): key is ConsolePermissionKey {
  return (CONSOLE_PERMISSIONS as readonly string[]).includes(key);
}

export function consolePermissionsOf(
  role: PlatformRoleKey | null,
): readonly ConsolePermissionKey[] {
  return role === null ? [] : CONSOLE_ROLE_PERMISSIONS[role];
}
